import React from 'react'

import { useExportResourceRecordsMutation } from '~/client/graph/operations/generated/Resources.generated'
import { ResourceExportFormat } from '~/client/graph/types.generated'
import { Button } from '~/client/src/shared/components/shadcn-ui/button'
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '~/client/src/shared/components/shadcn-ui/dialog'
import { Switch } from '~/client/src/shared/components/shadcn-ui/switch'
import Spinner from '~/client/src/shared/components/Spinner/Spinner'
import Localization from '~/client/src/shared/localization/LocalizationManager'
import type { ResourceSchema } from '~/client/src/shared/resources/domain/resourceSchema'

interface IProps {
  isOpen: boolean
  onClose: () => void
  projectId: string
  resourceTypeId: string
  properties: ResourceSchema['properties']
  loading: boolean
  propertiesLabel: (count: number) => string
}

export function ResourceExportDialog({
  isOpen,
  onClose,
  projectId,
  resourceTypeId,
  properties,
  loading,
  propertiesLabel,
}: IProps) {
  const [enabledProperties, setEnabledProperties] = React.useState<
    Record<string, boolean>
  >({})

  const [exportRecords, { loading: exportLoading }] =
    useExportResourceRecordsMutation()

  React.useEffect(() => {
    if (properties.length > 0) {
      const initialEnabled = properties.reduce(
        (acc, prop) => {
          acc[prop.name] = true
          return acc
        },
        {} as Record<string, boolean>,
      )
      setEnabledProperties(initialEnabled)
    }
  }, [properties])

  const handleToggleProperty = (propertyName: string) => {
    setEnabledProperties(prev => ({
      ...prev,
      [propertyName]: !prev[propertyName],
    }))
  }

  const handleConfirmExport = async () => {
    const enabledPropertyIds = properties
      .filter(prop => enabledProperties[prop.name] !== false)
      .map(prop => prop.id)
      .filter((id): id is string => !!id)

    try {
      const result = await exportRecords({
        variables: {
          projectId,
          input: {
            resourceTypeId,
            format: ResourceExportFormat.Csv,
            propertyIds: enabledPropertyIds,
          },
        },
      })

      const exportData = result.data?.exportResourceRecords
      if (exportData?.data && exportData?.filename) {
        const blob = new Blob([exportData.data], {
          type: exportData.mimeType || 'text/csv',
        })
        const url = URL.createObjectURL(blob)
        const link = document.createElement('a')
        link.href = url
        link.download = exportData.filename
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
        URL.revokeObjectURL(url)
      }

      onClose()
    } catch (error) {
      console.error('Export failed:', error)
    }
  }

  return (
    <Dialog open={isOpen} onOpenChange={open => !open && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{Localization.translator.export}</DialogTitle>
        </DialogHeader>
        <div className="tw:overflow-auto tw:max-h-[70vh]">
          {loading ? (
            <div>{Localization.translator.loadingProperties}</div>
          ) : (
            <div className="tw:flex tw:flex-col">
              <div className="tw:text-body-md tw:font-semibold tw:text-foreground tw:mb-2.5">
                {propertiesLabel(properties.length)}
              </div>
              <div className="tw:flex tw:flex-col">
                {properties.map(property => (
                  <div
                    key={property.name}
                    className="tw:flex tw:py-2 tw:justify-between tw:items-center tw:cursor-pointer"
                    onClick={() => handleToggleProperty(property.name)}
                  >
                    <span className="tw:text-body-md tw:text-foreground">
                      {property.displayName || property.name}
                    </span>
                    <Switch
                      className="tw:grow-0"
                      checked={enabledProperties[property.name] ?? true}
                      onCheckedChange={() =>
                        handleToggleProperty(property.name)
                      }
                    />
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            {Localization.translator.cancel}
          </Button>
          <Button
            variant="default"
            onClick={handleConfirmExport}
            disabled={exportLoading}
          >
            {exportLoading ? (
              <Spinner size="xs" />
            ) : (
              Localization.translator.export
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
